import cn from 'classnames';
import { useEffect, useRef, useState, useContext, SyntheticEvent } from 'react';
import { observer } from 'mobx-react';
import { Menu } from 'uiKit/atoms/Menu/Menu';
import { MenuItem } from 'uiKit/atoms/MenuItem/MenuItem';
import { Badge } from 'uiKit/atoms/Badge/Badge';
import { Box } from 'uiKit/atoms/Box/Box';
import { Button } from 'uiKit/atoms/Button/Button';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

import { ReactComponent as BNSLogoSvg } from 'assets/img/bns-logo.svg';
import { ReactComponent as ExpandMoreSvg } from '../../assets/img/expand-more.svg';
import { ReactComponent as HeartOutlinedSvg } from '../../assets/img/heart-outlined.svg';
import { ReactComponent as HeartOutlinedBlueSvg } from '../../assets/img/heart-outlined-blue.svg';
import { ReactComponent as MailFilled } from 'assets/img/mail-filled-blue.svg';
import { ReactComponent as MailSvg } from 'assets/img/mail.svg';
import { ReactComponent as PersonAdminSVG } from '../../assets/img/person-admin.svg';
import { ReactComponent as PersonAdminSVGBlue } from '../../assets/img/person-admin-blue.svg';
import { ReactComponent as PersonSVG } from '../../assets/img/person.svg';
import { ReactComponent as PlusSVG } from '../../assets/img/plus.svg';

import { ADMIN_PAGE, MENU_ITEMS, REDIRECT_AFTER_LOGIN_KEY, PAGE_INACTIVE_RELOAD_THRESHOLD_MS } from '../../constants';
import { useStores } from 'stores';
import { AuthContext, AuthContextProps } from 'context/AuthContext';
import { useMessanger } from 'hooks';
import styles from './styles.module.scss';
import { useCustomNavigation } from '../../hooks/useCustomNavigation';

const Header = () => {
  const [activeMenuItem, changeActiveMenuItem] = useState<string | null>(null);
  const hiddenAtRef = useRef<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { logout, photoUrl } = useContext<Partial<AuthContextProps>>(AuthContext);

  const {
    messageStore: { getUnreadConversationsCount, unreadConversationIndicator },
    userStore,
    lotStore,
  } = useStores();

  const { pathname } = useLocation();

  const navigate = useNavigate();
  useMessanger();

  useEffect(() => {
    getUnreadConversationsCount();
  }, []);

  const customNavigation = useCustomNavigation();

  const handleLinkClick = (e: SyntheticEvent, link: string) => {
    e.preventDefault();
    lotStore.setDefaultPage();
    customNavigation(link);
  };

  const logoutHandler = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (pathname !== '/') {
      sessionStorage.setItem(REDIRECT_AFTER_LOGIN_KEY, pathname + window.location.search);
    }
    try {
      if (logout) logout();
      await userStore.logoutUserWinAuth();
    } catch (e) {
      console.error('Logout failed', e);
      sessionStorage.removeItem(REDIRECT_AFTER_LOGIN_KEY);
    } finally {
      navigate('/');
    }
  };


  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        return;
      }

      if (document.visibilityState === 'visible' && hiddenAtRef.current) {
        const inactiveTime = Date.now() - hiddenAtRef.current;

        if (inactiveTime > PAGE_INACTIVE_RELOAD_THRESHOLD_MS) {
          window.location.reload();
        }

        hiddenAtRef.current = null;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    changeActiveMenuItem(pathname);
  }, [pathname]);

  const handleMenuClick = (e: SyntheticEvent, link: string) => {
    lotStore.resetFiltersParams();
    handleLinkClick(e, link);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const getLinkStyles = (link: string) =>
    activeMenuItem === link ? styles.bns3_user_profile_item_active : styles.bns3_user_profile_item;

  return (
    <header className={styles.bns3_header} onClick={() => lotStore.dismissToast()}>
      <div className={styles.bns3_mainContainer}>
        <Box display="flex" className={styles.bns3_navbar}>
          <Box className={styles.bns3_navbar_logo}>
            <RouterLink to="/" onClick={e => handleLinkClick(e, '/')}>
              <BNSLogoSvg />
            </RouterLink>
          </Box>

          <Box flex={1} className={styles.bns3_desktop_menu}>
            <Box display="flex" className={styles.bns3_navbar_icons_wrapper}>
              <Box className={styles.bns3_navbar_icons_container}>
                <Box display="flex" className={styles.bns3_navbar_icons}>
                  <Box className={styles.bns3_navbar_icon}>
                    <RouterLink
                      to="/favorites"
                      className={styles.bns3_navbar_icon_link}
                      onClick={e => handleLinkClick(e, '/favorites')}
                    >
                      {pathname === '/favorites' ? <HeartOutlinedBlueSvg /> : <HeartOutlinedSvg />}
                    </RouterLink>
                  </Box>

                  <Box className={styles.bns3_navbar_icon}>
                    <RouterLink
                      to="/conversations"
                      className={styles.bns3_navbar_icon_link}
                      onClick={e => handleLinkClick(e, '/conversations')}
                    >
                      <Badge color="error" variant="dot" invisible={!unreadConversationIndicator}>
                        {pathname.includes('conversation') ? <MailFilled /> : <MailSvg />}
                      </Badge>
                    </RouterLink>
                  </Box>

                  {userStore.isAdmin && (
                    <Box className={styles.bns3_navbar_icon}>
                      <RouterLink to={ADMIN_PAGE.href} className={styles.bns3_navbar_icon_link}>
                        {pathname === ADMIN_PAGE.href ? <PersonAdminSVGBlue /> : <PersonAdminSVG />}
                      </RouterLink>
                    </Box>
                  )}
                  <Box className={styles.bns3_navbar_addIcon}>
                    {pathname === '/create' || pathname.includes('/edit/') ? (
                      <div>DRAFT</div>
                    ) : (
                      <RouterLink to="/create">
                        <Button
                          sx={{ borderRadius: '99px', textTransform: 'none', fontWeight: 500, px: 2.5 }}
                          variant="outlined"
                          startIcon={<PlusSVG />}
                          size="large"
                        >
                          Add New Lot
                        </Button>
                      </RouterLink>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* AVATAR DROPDOWN  */}

              <Box className={styles.bns3_navbar_profile_container}>
                <Box onClick={handleMenuOpen} className={styles.bns3_navbar_profile_link} component="div">
                  {photoUrl ? (
                    <div className={styles.bns3_navbar_avatar_container}>
                      <img src={photoUrl} alt="Profile" className={styles.bns3_navbar_avatar} />
                    </div>
                  ) : (
                    <PersonSVG />
                  )}
                  <ExpandMoreSvg />
                </Box>

                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleMenuClose}
                  PaperProps={{ className: styles.bns3_user_profile_menu_container }}
                >
                  <Box className={styles.bns3_user_profile_menu}>
                    {MENU_ITEMS.map(({ children, link }) => (
                      <MenuItem
                        key={children}
                        component={RouterLink}
                        to={link}
                        onClick={e => handleMenuClick(e, link)}
                        className={getLinkStyles(link)}
                      >
                        {children}
                      </MenuItem>
                    ))}

                    <MenuItem
                      onClick={logoutHandler}
                      className={cn(styles.bns3_user_profile_item, styles.bns3_user_logout)}
                    >
                      Logout
                    </MenuItem>
                  </Box>
                </Menu>
              </Box>
            </Box>
          </Box>
        </Box>
      </div>
    </header>
  );
};

export default observer(Header);
